import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Phone, Users } from 'lucide-react';
import { apiGet } from '../../hooks/useApi';
import { useWebSocket } from '../../providers/WebSocketProvider';

interface QueueCountResponse {
  total?: number;
  calls?: number;
  sms?: number;
  /** The rep's OWN welcome calls workable today. */
  welcome_calls?: number;
  /** Distributed welcome calls across the rep's visible agency scope. */
  welcome_calls_distributed?: number;
}

interface WelcomeCallsCounterProps {
  /** Opens the team presence drawer — the Team pill is its entry point (#1469). */
  onOpenTeam: () => void;
}

/**
 * Compact queue/team badges for the global Header:
 *
 *  - "Welcome Calls: N" — the rep's own remaining welcome calls for TODAY.
 *    Queue-context only (Inbox, Contacts, Queue Console, Dashboard); it's
 *    noise on admin/reporting views, so it's hidden elsewhere.
 *  - "Team" — the entry point to the team presence drawer, shown on EVERY
 *    page (#1469). When welcome calls are distributed out to other reps it
 *    also surfaces the distributed total as "Team: N" (per #1046). Clicking
 *    it opens the drawer, replacing the old standalone Users icon.
 *
 * Polls on mount, refreshes when a `queue.updated` WS event arrives.
 */
export function WelcomeCallsCounter({ onOpenTeam }: WelcomeCallsCounterProps) {
  const [count, setCount] = useState<number | null>(null);
  const [distributed, setDistributed] = useState<number>(0);
  const location = useLocation();
  const { subscribe } = useWebSocket();

  // The rep's OWN welcome-calls pill is queue-context only — Inbox, Contacts,
  // Queue Console, Dashboard. On admin/reporting views the number is noise.
  // The Team pill below is not gated this way; it shows everywhere.
  const onQueuePage =
    location.pathname === '/inbox' ||
    location.pathname.startsWith('/contacts') ||
    location.pathname === '/console' ||
    location.pathname === '/dashboard';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await apiGet<QueueCountResponse>('/api/queue/count');
        if (!cancelled) {
          setCount(data.welcome_calls ?? 0);
          setDistributed(data.welcome_calls_distributed ?? 0);
        }
      } catch {
        // Silently no-op — Header should never blow up from this
      }
    };
    void load();
    const unsub = subscribe('queue.updated', () => void load());
    return () => {
      cancelled = true;
      unsub();
    };
  }, [subscribe]);

  // Distributed total includes the rep's own; only worth surfacing the count
  // on the Team pill when there are calls out to OTHER reps beyond this rep's
  // own load.
  const others = Math.max(distributed - (count ?? 0), 0);
  const showOwn = onQueuePage && (count ?? 0) > 0;

  return (
    <div className="flex items-center gap-1.5">
      {showOwn && (
        <div
          className="flex items-center gap-1.5 whitespace-nowrap bg-primary/10 text-primary border border-primary/20 px-2.5 py-1 rounded-lg text-[0.75rem] font-semibold"
          title={`${count} welcome call${count === 1 ? '' : 's'} pending in your queue today`}
        >
          <Phone className="w-3 h-3 stroke-[2]" />
          Welcome Calls: <span className="tabular-nums">{count}</span>
        </div>
      )}
      {/* Team pill — always visible; opens the team presence drawer. Shows the
          distributed welcome-calls total when calls are assigned out to other
          reps, otherwise just "Team". (#1469 folded the old standalone Team
          icon into this clickable pill.) */}
      <button
        type="button"
        onClick={onOpenTeam}
        title={
          others > 0
            ? `${distributed} welcome call${distributed === 1 ? '' : 's'} distributed across your team today (${others} assigned to other reps) — click to view team status`
            : 'View team status'
        }
        className="flex items-center gap-1.5 whitespace-nowrap bg-muted text-muted-foreground hover:bg-secondary hover:text-foreground border border-border px-2.5 py-1 rounded-lg text-[0.75rem] font-semibold transition-colors cursor-pointer"
      >
        <Users className="w-3 h-3 stroke-[2]" />
        {others > 0 ? (
          <>
            Team: <span className="tabular-nums">{distributed}</span>
          </>
        ) : (
          'Team'
        )}
      </button>
    </div>
  );
}
